import {
    Autocomplete,
    AutocompleteProps,
    Box,
    Button,
    ButtonProps,
    Checkbox,
    ChipTypeMap,
    CircularProgress,
    createFilterOptions,
    Skeleton,
    TextField,
    TextFieldProps,
    UseAutocompleteProps,
} from "@mui/material";
import {FinalForm, MuiRFF, useForm} from "../../form";
import React, {ElementType, useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useDebounced, useIsMounted} from "../../../index";
import {Utils} from "../../../../core";
import {AutocompleteRenderOptionState} from "@mui/material/Autocomplete/Autocomplete";


interface BaseAutocompleteTextFieldProps<
    Value,
    FetchOptions extends boolean,
    Form extends boolean,
> {
    form: Form;
    options?: FetchOptions extends true ? never : ReadonlyArray<Value>;
    getOptions?: FetchOptions extends false ? never : (keyword: string | undefined, abortController: AbortController) => Promise<Array<Value> | undefined>
    buttonKey?: number | string;
    onButtonClick?: AutocompleteTextFieldOptionProps<Value>['onButtonClick'];
    getOptionKey: (option: Value) => string | number;
    getOptionLabel: (option: Value) => string;
}

type MuiAutocompleteTextFieldProps<
    Value,
    FetchOptions extends boolean,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> = Omit<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>, 'options' | 'renderInput'>
    & BaseAutocompleteTextFieldProps<Value, FetchOptions, false> & {
    TextFieldProps?: TextFieldProps,
};

type FormAutocompleteTextFieldProps<
    Value,
    FetchOptions extends boolean,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> = Omit<MuiRFF.AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, 'options'>
    & BaseAutocompleteTextFieldProps<Value, FetchOptions, true>;

type AutocompleteTextFieldTypes = "MuiRFF" | "Mui";

export type AutocompleteTextFieldProps<
    Value,
    Type extends AutocompleteTextFieldTypes = "Mui",
    FetchOptions extends boolean = false,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined,
> = Type extends "Mui"
    ? MuiAutocompleteTextFieldProps<Value, FetchOptions, Multiple, DisableClearable, FreeSolo>
    : Type extends "MuiRFF"
        ? FormAutocompleteTextFieldProps<Value, FetchOptions, Multiple, DisableClearable, FreeSolo>
        : never;


const AutocompleteTextfield = <
    Value,
    Type extends AutocompleteTextFieldTypes = "Mui",
    FetchOptions extends boolean = false,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
>(props: AutocompleteTextFieldProps<Value, Type, FetchOptions, Multiple, DisableClearable, FreeSolo>) => {
    type UACProps = NonUndefined<UseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>>;
    type Props = AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>;

    const {getOptions, filterOptions: filterOptionsProps, onInputChange: onInputChangeProps, getOptionKey, getOptionLabel} = props;

    const [options, setOptions] = useState<Array<Value>>([]);
    const [loading, setLoading] = useState(!!props.getOptions);
    const [, setKeywords, deferredKeywords] = useDebounced("", 500);
    const isMounted = useIsMounted();

    const useApi = useMemo(() => !!getOptions, [getOptions]);

    /**
     * With each change in the [useApi] value:
     * - updates the options only if they exist
     */
    useLayoutEffect(() => {
        if (useApi)
            return;
        const options = props.options ?? [];
        const uniqueOptionKeys = [...new Set(options.map(getOptionKey) ?? []).values()];
        setOptions(uniqueOptionKeys.map((key) => options.find(e => getOptionKey(e) === key)!))
    }, [useApi, props.options, getOptionKey]);

    /**
     * With each change in the [useApi] value:
     * - updates the loading state.
     */
    useLayoutEffect(() => {
        setLoading(useApi)
    }, [useApi]);

    /**
     * Fetches the options from the api.
     */
    const fetchOptions = useCallback(async (abortController: AbortController) => {
        setLoading(true);
        const options = await getOptions!(deferredKeywords, abortController);
        if (!isMounted())
            return;
        if (!abortController?.signal?.aborted) {
            setLoading(false);
        }
        const uniqueOptionKeys = [...new Set(options?.map(getOptionKey) ?? []).values()];
        setOptions(uniqueOptionKeys.map((key) => options?.find(e => getOptionKey(e) === key)!));
    }, [getOptions, deferredKeywords, isMounted, getOptionKey]);

    /**
     * With each change in the [useApi] prop: (basically as soon as the component is mounted and the column has an api url)
     * - fetches the options.
     */
    useEffect(() => {
        if (!useApi)
            return;
        const abortController = new AbortController();
        fetchOptions(abortController).then();
        return () => abortController.abort();
    }, [fetchOptions, useApi]);

    const defaultLocalFilterOptions = useMemo<Required<UACProps>['filterOptions']>(() => createFilterOptions({
        ignoreCase: true,
        limit: 50,
        stringify: getOptionLabel,
    }), [getOptionLabel]);

    /**
     * Filters the autocomplete options based on the state and the provided prop config.
     */
    const filterOptions = useCallback<Required<UACProps>['filterOptions']>((options, state) => {
        if (filterOptionsProps) {
            return filterOptionsProps(options, state);
        }
        if (defaultLocalFilterOptions) {
            return defaultLocalFilterOptions(options, state);
        }
        return options;
    }, [filterOptionsProps, defaultLocalFilterOptions]);

    /**
     * Sets the keyword value when the value of the input changes.
     */
    const onInputChange = useCallback<Required<UACProps>['onInputChange']>((e, value, reason) => {
        if (onInputChangeProps) {
            onInputChangeProps(e, value, reason)
        }
        reason !== 'reset' && setKeywords(value ?? "")
    }, [onInputChangeProps, setKeywords])

    /**
     * Determines whether the option is equal to value or not.
     */
    const isOptionEqualToValue = useCallback<Required<UACProps>['isOptionEqualToValue']>((option, value) => getOptionKey(option) === getOptionKey(value), [getOptionKey])

    /**
     * Renders the option.
     */
    const renderOption = useCallback<Required<Props>['renderOption']>((optionProps, option, state) => {
        const {key, ...extraProps} = optionProps;
        return (
            <AutocompleteTextFieldOption
                key={key}
                props={extraProps}
                state={state}
                option={option}
                optionKey={key}
                optionLabel={getOptionLabel(option)}
                multiple={props.multiple}
                buttonKey={props.buttonKey}
                onButtonClick={props.onButtonClick}
            />
        );
    }, [getOptionLabel, props.buttonKey, props.multiple, props.onButtonClick])

    const loadingText = useMemo(() =>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {
                    Array.from({length: 8}).map((_, key) => (
                        <Skeleton
                            key={key}
                            width={'100%'}
                            sx={{minHeight: 30}}
                        />
                    ))
                }
            </Box>
        , [])

    const componentsProps = useMemo<typeof props['componentsProps']>(() => ({
        clearIndicator: {color: 'default'},
        popupIndicator: {color: 'default'},
    }), []);

    const {
        form: m_,
        TextFieldProps: m__,
        getOptions: m___,
        buttonKey: m____,
        onButtonClick: m_____,
        ...muiProps
    } = props as MuiAutocompleteTextFieldProps<Value, FetchOptions, Multiple, DisableClearable, FreeSolo>;
    const {
        form: f_,
        getOptions: f__,
        buttonKey: f___,
        onButtonClick: f____,
        ...formProps
    } = props as FormAutocompleteTextFieldProps<Value, FetchOptions, Multiple, DisableClearable, FreeSolo>;

    return (
        <>
            {
                !props.form &&
                <Autocomplete<Value, Multiple, DisableClearable, FreeSolo>
                    size={'small'}
                    loading={loading}
                    loadingText={loadingText}
                    isOptionEqualToValue={isOptionEqualToValue}
                    componentsProps={componentsProps}
                    renderOption={renderOption}
                    {...muiProps}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...props.TextFieldProps}
                            size={'small'}
                            {...(props.TextFieldProps ?? {})}
                            InputProps={{
                                ...params.InputProps,
                                ...(props.TextFieldProps?.InputProps ?? {}),
                                endAdornment: (
                                    <React.Fragment>
                                        {
                                            (loading)
                                                ? <CircularProgress color="inherit" size={15}/>
                                                : null
                                        }
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    }
                    options={options}
                    filterOptions={filterOptions}
                    onInputChange={onInputChange}
                />
            }
            {
                props.form &&
                <MuiRFF.Autocomplete<Value, Multiple, DisableClearable, FreeSolo>
                    size={'small'}
                    loading={loading}
                    loadingText={loadingText}
                    isOptionEqualToValue={isOptionEqualToValue}
                    componentsProps={componentsProps}
                    renderOption={renderOption}
                    {...formProps}
                    options={options}
                    filterOptions={filterOptions}
                    onInputChange={onInputChange}
                />
            }
        </>
    );
}


export interface AutocompleteTextFieldOptionProps<Value> {
    props: React.HTMLAttributes<HTMLLIElement>;
    state: AutocompleteRenderOptionState;
    option: Value;
    optionKey: string | number;
    optionLabel: string;
    multiple?: boolean;
    buttonKey?: number | string;
    onButtonClick?: (e: React.MouseEvent, form: FinalForm.FormApi | null, option: Value) => void;
}

export const AutocompleteTextFieldOption = <Value, >({
                                                         props,
                                                         option,
                                                         state,
                                                         multiple,
                                                         buttonKey,
                                                         onButtonClick,
                                                         optionKey,
                                                         optionLabel,
                                                     }: AutocompleteTextFieldOptionProps<Value>) => {
    const {form} = useForm() ?? {};

    /**
     * Modifies the event to not propagate and calls the provided [onButtonClick] prop.
     */
    const onClick = useCallback<Required<ButtonProps>['onClick']>((e) => {
        Utils.modifyEvent(e);
        if (onButtonClick) {
            onButtonClick(e, form ?? null, option);
        }
    }, [onButtonClick, form, option])

    return (
        <>
            <Box component={'li'} {...props} sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
                {
                    optionKey === buttonKey
                        ? <Button
                            variant={'contained'}
                            sx={{margin: 'auto', width: '100%'}}
                            onClick={onClick}
                        >
                            {optionLabel ?? ""}
                        </Button>
                        : <>
                            {multiple && <Checkbox size={'small'} readOnly checked={state.selected}/>}
                            {optionLabel ?? ""}
                        </>
                }
            </Box>
        </>
    );
}

export default AutocompleteTextfield;
